import React, { FC, useState } from "react";
import { ModalDialog } from "../../../../components";
import { Icon, RadioGroup } from "@telia/teddy";
import _get from "lodash/get";
import { useDispatch } from "react-redux";
import { CUSTOMER_TYPE } from "../../../../../../constants";
import { ICartItemWithBundleId } from "../../../../../../models";
import { logToSentry } from "../../../../../../server/operations/sentryLogger";
import { removeHardwareItemFromBundle } from "../../../state/actions/cartActions";
import "./SmartDiscountModal.less";

const MODAL_STATE = {
    INITIAL: "Initial",
    REMOVED: "Has removed",
    KEEP: "Keep",
};

const modalOptions = {
    KEEP: "Keep",
    REMOVE_HARDWARE: "Remove hardware",
};

interface RemoveConfirmationModalProps {
    onClose: () => void;
    productName: string;
}

interface ConfirmationModalProps extends RemoveConfirmationModalProps {
    onClose: () => void;
    price?: number;
    productName: string;
}

interface SmartDiscountModalProps {
    currentBundleItem?: ICartItemWithBundleId;
    discountCustomerType?: string;
    handleOnContinue: () => void;
    onClose: () => void;
}

const KeepConfirmationModal: FC<ConfirmationModalProps> = ({ onClose, productName, price }) => {
    return (
        <ModalDialog
            size="medium"
            name="keep-confirmation-modal"
            submitText="Oppdater handlekurv"
            submitKind="secondary"
            onClose={onClose}
            closeKind="primary"
            closeText="OK"
        >
            <div className="keep-confirmation-modal">
                <Icon name="check-circle" className="keep-confirmation-modal__icon" />
                <span>
                    {productName} blir værende i handlekurven til {price},- med 12 md. avtaletid{" "}
                </span>
            </div>
        </ModalDialog>
    );
};

const RemoveConfirmationModal: FC<RemoveConfirmationModalProps> = ({ onClose, productName }) => {
    return (
        <ModalDialog
            size="medium"
            name="remove-confirmation-modal"
            submitText="Oppdater handlekurv"
            submitKind="secondary"
            onClose={onClose}
            closeKind="primary"
            closeText="OK"
        >
            <div className="remove-confirmation-modal">
                <Icon name="check-circle" className="remove-confirmation-modal__icon" />
                <span>{productName} er fjernet fra handlekurven</span>
            </div>
        </ModalDialog>
    );
};

const getIngress = (discountCustomerType?: string): string => {
    switch (discountCustomerType) {
        case CUSTOMER_TYPE.EXISTING:
            return "Tilbudet gjelder når du tegner et nytt abonnement, men du får likevel en god pris hvis du binder abonnementet ditt i 12 måneder.";
        case CUSTOMER_TYPE.NEW:
            logToSentry("Showing modal that simdeal doesn't apply for new customers. Probably wrong behaviour?");
            return "Pakketilbudet gjelder dessverre ikke for nye Telia-abonnement, men du kan få en god pris ved å velge 12 md. avtaletid på abonnementet.";
        default:
            return "Du kan dessverre ikke få valgt pakketilbud, men du kan allikevel få en god pris ved å velge 12 md. avtaletid på abonnementet.";
    }
};

const SmartDiscountModal: FC<SmartDiscountModalProps> = ({
    currentBundleItem,
    onClose,
    discountCustomerType,
    handleOnContinue,
}) => {
    const dispatch = useDispatch();
    const [selectedSmartDiscountOption, setSelectedSmartDiscountOption] = useState(modalOptions.KEEP);
    const [modalState, setModalState] = useState(MODAL_STATE.INITIAL);
    const [removedProductName, setRemovedProductName] = useState("");

    if (!currentBundleItem) {
        return null;
    }

    const productName = currentBundleItem.name;
    const price = _get(currentBundleItem, "price.total");
    const originalSalesPrice = _get(currentBundleItem, "price.originalSalesPrice");
    if (modalState === MODAL_STATE.REMOVED) {
        return <RemoveConfirmationModal onClose={onClose} productName={removedProductName} />;
    } else if (modalState === MODAL_STATE.KEEP) {
        return <KeepConfirmationModal onClose={onClose} productName={productName} price={price} />;
    }

    const handleSubmit = async () => {
        if (selectedSmartDiscountOption === modalOptions.KEEP) {
            handleOnContinue();
            setModalState(MODAL_STATE.KEEP);
        } else {
            setRemovedProductName(currentBundleItem.name);
            await dispatch(removeHardwareItemFromBundle(currentBundleItem));
            setModalState(MODAL_STATE.REMOVED);
        }
    };
    return (
        <ModalDialog
            size="medium"
            name="empty-cart-last-item-modal"
            submitText="Oppdater handlekurv"
            submitKind="secondary"
            closeText="Avbryt"
            closeKind="text-underline"
            onSubmit={handleSubmit}
            onClose={onClose}
        >
            <span>{getIngress(discountCustomerType)}</span>
            <RadioGroup value={selectedSmartDiscountOption} onValueChange={setSelectedSmartDiscountOption}>
                <RadioGroup.Item>
                    <RadioGroup.Trigger value={modalOptions.KEEP} />
                    <RadioGroup.Label>
                        {`Ja, jeg vil ha ${productName} til ${price},- (opprinnelig ${originalSalesPrice},-) med 12 md. avtaletid`}
                    </RadioGroup.Label>
                </RadioGroup.Item>
                <RadioGroup.Item>
                    <RadioGroup.Trigger value={modalOptions.REMOVE_HARDWARE} />
                    <RadioGroup.Label>Nei takk, da tar jeg bare abonnementet</RadioGroup.Label>
                </RadioGroup.Item>
            </RadioGroup>
        </ModalDialog>
    );
};

export default SmartDiscountModal;
